import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { Download, FileEarmarkCode, PlayFill } from 'react-bootstrap-icons';

import ProjectChatPanel from '../chat/ProjectChatPanel';
import {
    compareRequirementTestCasesWithAdo,
    fetchAdoCompareSyncJob,
    fetchLatestAdoCompareSyncJob,
    fetchLatestRequirementAdoPushJob,
    fetchRequirementAdoPushJob,
    fetchRequirementTestDetail,
    fetchRequirementLiveTestWorkspace,
    fetchRequirementTestWorkspace,
    generateGherkin,
    startAdoCompareChangesSync,
    startRequirementTestCasesAdoPush,
    deleteRequirementTestCase,
    deleteRequirementTestFolder,
    renameRequirementTestFolder,
    updateRequirementTestFolder,
} from '../../api/requirements';
import { useProjects } from '../../contexts/ProjectContext';
import {
    RequirementArtifact,
    AdoCompareChange,
    AdoCompareResponse,
    AdoTestStatusOverride,
    RequirementAdoCompareSyncJob,
    RequirementAdoPushJob,
    RequirementGenerationJob,
    RequirementTestDetailResponse,
    RequirementTestFolderNode,
    RequirementTestWorkspaceResponse,
    RequirementWorkspaceTestCase,
} from '../../types/requirements';
import { formatLocalDateTimeFromUTC } from '../../utils/datetime';
import { projectArtifactDownloadFilename, triggerDownloadFromBlob } from '../../utils/download';
import { normalizeJobType } from '../../utils/jobType';
import { buildTestCaseCsvExport, TestCaseExportFormat } from '../../utils/testCaseExport';
import TestCasePopup from './TestCasePopup';
import TestDirectoryTree from './TestDirectoryTree';
import TestResultsList from './TestResultsList';
import AddTestCaseModal from './AddTestCaseModal';
import FolderPathSelector, { formatFolderPathForDisplay, normalizeFolderPathInput } from './FolderPathSelector';

import './RequirementTestWorkspace.css';

type MoveDialogState = {
    open: boolean;
    test?: RequirementWorkspaceTestCase | null;
    tests?: RequirementWorkspaceTestCase[];
    value: string;
    saving: boolean;
    error?: string | null;
};

type RenameDialogState = {
    open: boolean;
    folder?: RequirementTestFolderNode | null;
    value: string;
    saving: boolean;
    error?: string | null;
};

type DeleteTestDialogState = {
    open: boolean;
    test?: RequirementWorkspaceTestCase | null;
    saving: boolean;
    error?: string | null;
};

type DeleteFolderDialogState = {
    open: boolean;
    folder?: RequirementTestFolderNode | null;
    saving: boolean;
    error?: string | null;
};

export interface RequirementTestWorkspaceProps {
    projectId: number;
    projectName?: string | null;
    selectedJobId?: number | null;
    onSelectedJobIdChange?: (jobId: number | null) => void;
    showJobSelector?: boolean;
    variant?: 'page' | 'embedded';
    topContent?: React.ReactNode;
    /** If set, auto-open the popup for this test key once the workspace data loads */
    initialTestKey?: string | null;
}

const WORKSPACE_POLL_INTERVAL_MS = 30_000;
const JOB_POLL_INTERVAL_MS = 5_000;
const ADO_PUSH_POLL_INTERVAL_MS = 2_000;
const ADO_COMPARE_SYNC_POLL_INTERVAL_MS = 2_000;

type AdoCompareChangePresentation = {
    title: string;
    meta: string[];
    values: string[];
    primaryActionLabel: string;
    primaryActionDisabled: boolean;
    primaryActionVariant: 'primary' | 'outline-primary' | 'outline-secondary';
};

function presentAdoCompareChange(
    change: AdoCompareChange,
    isSyncing: boolean,
): AdoCompareChangePresentation {
    const folderId = String(change.folder_id || change.ado_value || '');

    if (change.type === 'stale_suite') {
        return {
            title: 'Delete old ADO folder from Test Plan',
            meta: folderId ? [`ADO folder: ${folderId}`] : [],
            values: ['This folder no longer exists in AcceliQA. Sync will remove this ADO suite and its test points from the managed Test Plan. ADO work items will not be deleted.'],
            primaryActionLabel: 'Delete',
            primaryActionDisabled: isSyncing,
            primaryActionVariant: 'primary',
        };
    }

    if (change.type === 'stale_suite_not_empty') {
        return {
            title: 'Delete old ADO folder from Test Plan',
            meta: folderId ? [`ADO folder: ${folderId}`] : [],
            values: ['This folder no longer exists in AcceliQA. Sync will remove this ADO suite and its test points from the managed Test Plan. ADO work items will not be deleted.'],
            primaryActionLabel: 'Delete',
            primaryActionDisabled: isSyncing,
            primaryActionVariant: 'primary',
        };
    }

    return {
        title: change.action || change.type,
        meta: [
            change.test_key || '',
            change.folder_id || '',
            change.ado_work_item_id ? `ADO #${change.ado_work_item_id}` : '',
        ].filter(Boolean),
        values: [
            `Local: ${change.local_value || '-'}`,
            `ADO: ${change.ado_value || '-'}`,
        ],
        primaryActionLabel: 'Sync',
        primaryActionDisabled: isSyncing,
        primaryActionVariant: 'primary',
    };
}

function isActiveGenerationStatus(status?: string | null): boolean {
    return ['queued', 'pending', 'running', 'retrying'].includes((status || '').toLowerCase());
}

function findFolderById(
    root: RequirementTestFolderNode | undefined | null,
    folderId: string | null | undefined,
): RequirementTestFolderNode | null {
    if (!root || !folderId) {
        return null;
    }

    if (root.id === folderId) {
        return root;
    }

    for (const child of root.children) {
        const match = findFolderById(child, folderId);
        if (match) {
            return match;
        }
    }

    return null;
}

function isTestInFolder(testCase: RequirementWorkspaceTestCase, folder: RequirementTestFolderNode | null): boolean {
    if (!folder) {
        return true;
    }

    const folderPath = folder.path || [];
    const testPath = testCase.folder_path || [];

    return folderPath.every((segment, index) => testPath[index] === segment);
}

function isSyntheticDataJob(job: RequirementGenerationJob | null | undefined): boolean {
    return normalizeJobType((job as any)?.job_type || job?.generation_kind) === 'synthetic_data';
}

export function getTestArtifact(job: RequirementGenerationJob | null): RequirementArtifact | null {
    if (!job?.artifacts?.length) {
        return null;
    }

    return job.artifacts.find((artifact) => (artifact.artifact_type || '').toLowerCase() === 'test_cases') || null;
}

function getAdoPushErrorMessage(error: any): string {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
        return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
        return detail.map((item) => item?.msg || item?.message || String(item)).join('; ');
    }
    if (error?.response?.status) {
        return `Azure DevOps push failed with HTTP ${error.response.status}. Check the ADO project settings and backend logs.`;
    }
    return error?.message || 'Failed to push test cases to Azure DevOps.';
}

function RequirementTestWorkspace({
    projectId,
    projectName,
    selectedJobId,
    onSelectedJobIdChange,
    showJobSelector = true,
    variant = 'page',
    topContent,
    initialTestKey,
}: RequirementTestWorkspaceProps): React.ReactElement {
    const navigate = useNavigate();
    const {
        requirementJobs,
        fetchRequirementJobs,
        downloadRequirementArtifact,
        projects,
        refreshProject,
    } = useProjects();

    const [internalSelectedJobId, setInternalSelectedJobId] = useState<number | null>(selectedJobId ?? null);
    const [pageLoading, setPageLoading] = useState<boolean>(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [workspace, setWorkspace] = useState<RequirementTestWorkspaceResponse | null>(null);
    const [workspaceLoading, setWorkspaceLoading] = useState<boolean>(false);
    const [workspaceError, setWorkspaceError] = useState<string | null>(null);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
    // Feature 7: popup preview state (replaces inline right-column detail)
    const [previewTestKey, setPreviewTestKey] = useState<string | null>(null);
    const [previewDetail, setPreviewDetail] = useState<RequirementTestDetailResponse | null>(null);
    const [previewLoading, setPreviewLoading] = useState<boolean>(false);
    const [previewDetailError, setPreviewDetailError] = useState<string | null>(null);
    // Feature 7: manual add modal
    const [showAddTestCaseModal, setShowAddTestCaseModal] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [lastRunFilter, setLastRunFilter] = useState<string>('all');
    const [sortBy, setSortBy] = useState<string>('priority');
    const [selectedTestKeys, setSelectedTestKeys] = useState<Set<string>>(() => new Set());
    const [selectionInitialized, setSelectionInitialized] = useState<boolean>(false);
    const [adoPushJob, setAdoPushJob] = useState<RequirementAdoPushJob | null>(null);
    const [adoCompareLoading, setAdoCompareLoading] = useState<boolean>(false);
    const [adoComparison, setAdoComparison] = useState<AdoCompareResponse | null>(null);
    const [adoCompareSyncJob, setAdoCompareSyncJob] = useState<RequirementAdoCompareSyncJob | null>(null);
    const [ignoredAdoChangeIds, setIgnoredAdoChangeIds] = useState<Set<string>>(() => new Set());
    const [syncingAdoChangeIds, setSyncingAdoChangeIds] = useState<Set<string>>(() => new Set());
    const syncingAdoChangeIdsRef = useRef<Set<string>>(new Set());
    const finalizedAdoPushJobIdsRef = useRef<Set<number>>(new Set());
    const finalizedAdoCompareSyncJobIdsRef = useRef<Set<number>>(new Set());
    const [gherkinLoading, setGherkinLoading] = useState<boolean>(false);
    const [actionAlert, setActionAlert] = useState<{ variant: 'success' | 'danger' | 'info'; text: string } | null>(null);
    const [moveDialog, setMoveDialog] = useState<MoveDialogState>({
        open: false,
        test: null,
        tests: [],
        value: '',
        saving: false,
        error: null,
    });
    const [renameDialog, setRenameDialog] = useState<RenameDialogState>({
        open: false,
        folder: null,
        value: '',
        saving: false,
        error: null,
    });
    const [deleteTestDialog, setDeleteTestDialog] = useState<DeleteTestDialogState>({
        open: false,
        test: null,
        saving: false,
        error: null,
    });
    const [deleteFolderDialog, setDeleteFolderDialog] = useState<DeleteFolderDialogState>({
        open: false,
        folder: null,
        saving: false,
        error: null,
    });

    const effectiveSelectedJobId = selectedJobId !== undefined ? selectedJobId : internalSelectedJobId;

    const project = useMemo(
        () => projects.find((candidate) => candidate.id === projectId) ?? null,
        [projectId, projects],
    );

    const resolvedProjectName = projectName || project?.name || `Project ${projectId}`;
    const isAzureProject = project?.project_type === 'azure_devops';

    const jobs = useMemo<RequirementGenerationJob[]>(
        () =>
            (requirementJobs[projectId] ?? [])
                .slice()
                .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime()),
        [projectId, requirementJobs],
    );
    const workspaceJobs = useMemo<RequirementGenerationJob[]>(
        () => jobs.filter((job) => !isSyntheticDataJob(job)),
        [jobs],
    );

    const selectedJob = useMemo(
        () => workspaceJobs.find((job) => job.id === effectiveSelectedJobId) ?? null,
        [effectiveSelectedJobId, workspaceJobs],
    );

    const selectedArtifact = useMemo(
        () => getTestArtifact(selectedJob),
        [selectedJob],
    );
    const selectedJobStableId = selectedJob?.id ?? null;
    const selectedJobProjectId = selectedJob?.project_id ?? null;
    const selectedArtifactStableId = selectedArtifact?.id ?? null;
    const hasLiveWorkspace = Boolean(
        selectedJob?.live_results &&
        isActiveGenerationStatus(selectedJob.status)
    );
    const canUseWorkspace = Boolean(selectedArtifact || hasLiveWorkspace);
    const isLiveWorkspace = Boolean(workspace?.live || (!selectedArtifact && hasLiveWorkspace));
    const liveResults = selectedJob?.live_results ?? null;
    const liveVersionLabel = selectedJob
        ? (selectedJob.version_number ? `v${selectedJob.version_number}` : `#${selectedJob.id}`)
        : '';
    // Phase A of the diff-first workflow (progress ~45-60) extracts requirements in a
    // single pass and emits no per-story checkpoints yet; Phase B (>=60) streams test
    // cases one user story at a time. Used to label the pre-checkpoint waiting state.
    const isRequirementsPhase = (selectedJob?.progress ?? 0) < 60;
    const adoPushActive = Boolean(adoPushJob && ['queued', 'running'].includes((adoPushJob.status || '').toLowerCase()));
    const adoPushInProgress = adoPushActive ? (adoPushJob?.scope === 'all' ? 'all' : 'selected') : null;

    const selectedFolder = useMemo(
        () => findFolderById(workspace?.directory, selectedFolderId || workspace?.default_folder_id || null),
        [selectedFolderId, workspace?.default_folder_id, workspace?.directory],
    );

    const selectedTests = useMemo(
        () => (workspace?.tests || []).filter((testCase) => selectedTestKeys.has(testCase.test_key)),
        [selectedTestKeys, workspace?.tests],
    );

    const highlightedFolderIds = useMemo(() => {
        const selectedFolderIds = new Set<string>();
        if (!workspace?.directory || selectedTests.length === 0) {
            return selectedFolderIds;
        }

        const collectHighlightedFolders = (folder: RequirementTestFolderNode): void => {
            if (selectedTests.some((testCase) => isTestInFolder(testCase, folder))) {
                selectedFolderIds.add(folder.id);
            }
            folder.children.forEach(collectHighlightedFolders);
        };

        collectHighlightedFolders(workspace.directory);
        return selectedFolderIds;
    }, [selectedTests, workspace?.directory]);

    const exportTests = useMemo(
        () => (selectedTests.length > 0 ? selectedTests : (workspace?.tests || [])),
        [selectedTests, workspace?.tests],
    );

    const unsyncedCount = useMemo(
        () => (workspace?.tests || []).filter((tc) => (
            tc.ado_push_status === 'not_pushed' ||
            tc.ado_push_status === 'updated' ||
            tc.ado_push_status === 'missing'
        )).length,
        [workspace?.tests],
    );

    const visibleAdoChanges = useMemo(
        () => (adoComparison?.changes || []).filter((change) => !ignoredAdoChangeIds.has(change.id)),
        [adoComparison?.changes, ignoredAdoChangeIds],
    );

    const adoChangesByGroup = useMemo(() => {
        const grouped = new Map<string, AdoCompareChange[]>();
        visibleAdoChanges.forEach((change) => {
            const group = change.group || 'Other';
            grouped.set(group, [...(grouped.get(group) || []), change]);
        });
        return Array.from(grouped.entries());
    }, [visibleAdoChanges]);

    const syncableVisibleAdoChanges = useMemo(
        () => visibleAdoChanges,
        [visibleAdoChanges],
    );

    const visibleAdoChangeIds = useMemo(
        () => syncableVisibleAdoChanges.map((change) => change.id),
        [syncableVisibleAdoChanges],
    );

    const allVisibleAdoChangesSyncing = useMemo(
        () => visibleAdoChangeIds.length > 0 && visibleAdoChangeIds.every((changeId) => syncingAdoChangeIds.has(changeId)),
        [syncingAdoChangeIds, visibleAdoChangeIds],
    );

    const hasSyncableVisibleAdoChanges = useMemo(
        () => visibleAdoChangeIds.some((changeId) => !syncingAdoChangeIds.has(changeId)),
        [syncingAdoChangeIds, visibleAdoChangeIds],
    );

    const adoStatusOverrides = useMemo<Record<string, AdoTestStatusOverride>>(
        () => ({
            ...(workspace?.test_status_overrides || {}),
            ...(adoComparison?.test_status_overrides || {}),
        }),
        [adoComparison?.test_status_overrides, workspace?.test_status_overrides],
    );
    const adoPushProgressPercent = useMemo(() => {
        if (!adoPushJob?.total_count) {
            return 0;
        }
        return Math.min(100, Math.round(((adoPushJob.processed_count || 0) / adoPushJob.total_count) * 100));
    }, [adoPushJob]);
    const adoPushCurrentLabel = useMemo(() => {
        const currentKey = adoPushJob?.current_test_key;
        if (!currentKey) {
            return null;
        }
        const test = (workspace?.tests || []).find((candidate) => candidate.test_key === currentKey);
        return test?.title || currentKey;
    }, [adoPushJob?.current_test_key, workspace?.tests]);
    const adoPushStructureMessages = useMemo(
        () => adoPushJob?.result_summary?.ado_structure?.messages?.filter(Boolean) || [],
        [adoPushJob?.result_summary?.ado_structure?.messages],
    );
    const adoCompareSyncActive = Boolean(adoCompareSyncJob && ['queued', 'running'].includes((adoCompareSyncJob.status || '').toLowerCase()));
    const adoCompareSyncProgressPercent = useMemo(() => {
        if (!adoCompareSyncJob?.total_count) {
            return 0;
        }
        return Math.min(100, Math.round(((adoCompareSyncJob.processed_count || 0) / adoCompareSyncJob.total_count) * 100));
    }, [adoCompareSyncJob]);
    const adoCompareSyncCurrentLabel = useMemo(() => {
        if (!adoCompareSyncJob?.current_change_id) {
            return null;
        }
        return adoCompareSyncJob.current_action || adoCompareSyncJob.current_change_id;
    }, [adoCompareSyncJob?.current_action, adoCompareSyncJob?.current_change_id]);

    const refreshAdoBackedProjectState = useCallback(async () => {
        if (!Number.isFinite(projectId)) {
            return;
        }

        try {
            await refreshProject(projectId);
        } catch {
            // Best-effort only; workspace state is still refreshed independently.
        }
    }, [projectId, refreshProject]);

    const setSelectedJob = useCallback(
        (jobId: number | null) => {
            if (selectedJobId === undefined) {
                setInternalSelectedJobId(jobId);
            }

            onSelectedJobIdChange?.(jobId);
        },
        [onSelectedJobIdChange, selectedJobId],
    );

    const filteredTests = useMemo(() => {
        const candidates = (workspace?.tests || []).filter((testCase) => isTestInFolder(testCase, selectedFolder));
        const query = search.trim().toLowerCase();
        const now = Date.now();

        const matches = candidates.filter((testCase) => {
            if (query) {
                const haystack = [
                    testCase.test_key,
                    testCase.title,
                    testCase.description,
                    testCase.linked_epic?.title,
                    testCase.linked_user_story?.title,
                    (testCase.folder_path || []).join(' '),
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();

                if (!haystack.includes(query)) {
                    return false;
                }
            }

            if (statusFilter !== 'all' && testCase.latest_status !== statusFilter) {
                return false;
            }

            if (lastRunFilter === 'never' && testCase.last_run) {
                return false;
            }

            if (lastRunFilter === 'active' && !testCase.last_run) {
                return false;
            }

            if (lastRunFilter === 'recent') {
                const createdAt = testCase.last_run?.created_at ? new Date(testCase.last_run.created_at).getTime() : 0;
                if (!createdAt || now - createdAt > 7 * 24 * 60 * 60 * 1000) {
                    return false;
                }
            }

            return true;
        });

        const sorted = [...matches];
        sorted.sort((left, right) => {
            if (sortBy === 'title') {
                return String(left.title || left.test_key).localeCompare(String(right.title || right.test_key));
            }

            if (sortBy === 'latest') {
                const leftValue = left.last_run?.created_at ? new Date(left.last_run.created_at).getTime() : 0;
                const rightValue = right.last_run?.created_at ? new Date(right.last_run.created_at).getTime() : 0;
                return rightValue - leftValue;
            }

            if (sortBy === 'failure') {
                const statusWeight = (status: string): number => {
                    if (status === 'failed') {
                        return 0;
                    }

                    if (isActiveGenerationStatus(status)) {
                        return 1;
                    }

                    if (status === 'never_run') {
                        return 2;
                    }

                    return 3;
                };

                return statusWeight(left.latest_status) - statusWeight(right.latest_status);
            }

            const leftPriority = Number(left.priority ?? 999);
            const rightPriority = Number(right.priority ?? 999);

            if (leftPriority !== rightPriority) {
                return leftPriority - rightPriority;
            }

            return String(left.title || left.test_key).localeCompare(String(right.title || right.test_key));
        });

        return sorted;
    }, [lastRunFilter, search, selectedFolder, sortBy, statusFilter, workspace?.tests]);

    const allVisibleSelected = useMemo(
        () => filteredTests.length > 0 && filteredTests.every((testCase) => selectedTestKeys.has(testCase.test_key)),
        [filteredTests, selectedTestKeys],
    );

    const loadWorkspace = useCallback(
        async (showLoader = true) => {
            if (!selectedJobStableId || !selectedJobProjectId || (!selectedArtifactStableId && !hasLiveWorkspace)) {
                setWorkspace(null);
                return;
            }

            if (showLoader) {
                setWorkspaceLoading(true);
            }
            setWorkspaceError(null);

            try {
                const response = selectedArtifactStableId
                    ? await fetchRequirementTestWorkspace(
                        selectedJobProjectId,
                        selectedJobStableId,
                        selectedArtifactStableId,
                    )
                    : await fetchRequirementLiveTestWorkspace(
                        selectedJobProjectId,
                        selectedJobStableId,
                    );
                // If the artifact content changed (e.g. a new generation updated the same artifact
                // in-place while polling was running), reset selectionInitialized so the
                // auto-selection logic re-runs and selects the new/unsynced test cases.
                setWorkspace((prev) => {
                    if (
                        prev &&
                        (prev.generation_id !== response.generation_id ||
                            prev.artifact_id !== response.artifact_id ||
                            prev.tests.length !== response.tests.length)
                    ) {
                        setSelectionInitialized(false);
                    }
                    return response;
                });
            } catch (error: any) {
                setWorkspaceError(error?.response?.data?.detail || error?.message || 'Failed to load test workspace.');
            } finally {
                if (showLoader) {
                    setWorkspaceLoading(false);
                }
            }
        },
        [hasLiveWorkspace, selectedArtifactStableId, selectedJobProjectId, selectedJobStableId],
    );

    const loadPreviewDetail = useCallback(
        async (testKey: string, showLoader = true) => {
            if (!selectedJob || !selectedArtifact) {
                setPreviewDetail(null);
                setPreviewDetailError('Full test details are available after the final test-case artifact is created.');
                return;
            }

            if (showLoader) {
                setPreviewLoading(true);
            }
            setPreviewDetailError(null);

            try {
                const response = await fetchRequirementTestDetail(
                    selectedJob.project_id,
                    selectedJob.id,
                    selectedArtifact.id,
                    testKey,
                );
                setPreviewDetail(response);
            } catch (error: any) {
                setPreviewDetailError(error?.response?.data?.detail || error?.message || 'Failed to load test detail.');
            } finally {
                if (showLoader) {
                    setPreviewLoading(false);
                }
            }
        },
        [selectedArtifact, selectedJob],
    );

    useEffect(() => {
        if (!Number.isFinite(projectId)) {
            setPageError('Invalid project identifier.');
            setPageLoading(false);
            return;
        }

        let active = true;
        setPageLoading(true);
        setPageError(null);

        Promise.allSettled([
            fetchRequirementJobs(projectId),
            project ? Promise.resolve(project) : refreshProject(projectId),
        ])
            .then((results) => {
                if (!active) {
                    return;
                }

                const rejected = results.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined;
                if (rejected) {
                    setPageError(rejected.reason?.response?.data?.detail || rejected.reason?.message || 'Failed to load project results.');
                }
            })
            .finally(() => {
                if (active) {
                    setPageLoading(false);
                }
            });

        return () => {
            active = false;
        };
        // Project hydration is intentionally excluded here because including it
        // re-triggers the initial workspace load after refreshProject resolves.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRequirementJobs, projectId, refreshProject]);

    useEffect(() => {
        // Prefer the newest active test-case generation so live checkpoints appear while
        // the job is still running. Synthetic-data jobs should not replace the visible
        // test-case workspace.
        const activeJob =
            workspaceJobs.find((job) => isActiveGenerationStatus(job.status) && Boolean(job.live_results)) ??
            workspaceJobs.find((job) => isActiveGenerationStatus(job.status)) ??
            null;
        const preferredJob =
            activeJob ??
            workspaceJobs.find((job) => job.status === 'completed' && Boolean(getTestArtifact(job))) ??
            workspaceJobs.find((job) => Boolean(getTestArtifact(job))) ??
            workspaceJobs[0] ??
            null;

        if (!preferredJob) {
            setSelectedJob(null);
            return;
        }

        const selectedJob = workspaceJobs.find((job) => job.id === effectiveSelectedJobId) ?? null;
        // Once the user has selected a snapshot, never replace it merely
        // because another generation became active.  The previous behavior
        // silently switched Version 1 to Version 2, making V1 test cases and
        // their authored steps appear to disappear.
        const shouldPreferActiveJob = Boolean(
            activeJob &&
            !effectiveSelectedJobId &&
            (!selectedJob || selectedJob.id !== activeJob.id)
        );

        if (!effectiveSelectedJobId || !selectedJob || shouldPreferActiveJob) {
            setSelectedJob(preferredJob.id);
        }
    }, [effectiveSelectedJobId, setSelectedJob, workspaceJobs]);

    useEffect(() => {
        setWorkspace(null);
        setPreviewDetail(null);
        setPreviewDetailError(null);
        setSelectedFolderId(null);
        setPreviewTestKey(null);
        setSelectedTestKeys(new Set());
        setSelectionInitialized(false);
        setActionAlert(null);
        setAdoComparison(null);
        setAdoCompareSyncJob(null);
        setIgnoredAdoChangeIds(new Set());
        setAdoPushJob(null);
        finalizedAdoPushJobIdsRef.current = new Set();
        finalizedAdoCompareSyncJobIdsRef.current = new Set();
        syncingAdoChangeIdsRef.current = new Set();
        setSyncingAdoChangeIds(new Set());

        if (selectedJobStableId && canUseWorkspace) {
            void loadWorkspace();
        }
    }, [canUseWorkspace, loadWorkspace, selectedArtifactStableId, selectedJobStableId]);

    useEffect(() => {
        if (!workspace) {
            return;
        }

        const currentFolder = findFolderById(workspace.directory, selectedFolderId || workspace.default_folder_id);
        if (!currentFolder) {
            setSelectedFolderId(workspace.default_folder_id);
        }
    }, [selectedFolderId, workspace]);

    useEffect(() => {
        const validKeys = new Set((workspace?.tests || []).map((testCase) => testCase.test_key));

        if (!selectionInitialized && validKeys.size > 0) {
            setSelectionInitialized(true);
            return;
        }

        setSelectedTestKeys((previous) => {
            if (previous.size === 0) {
                return previous;
            }

            const next = new Set(Array.from(previous).filter((testKey) => validKeys.has(testKey)));
            return next.size === previous.size ? previous : next;
        });
    }, [selectionInitialized, workspace?.tests]);

    useEffect(() => {
        if (filteredTests.length === 0 && previewTestKey && !filteredTests.some((tc) => tc.test_key === previewTestKey)) {
            setPreviewTestKey(null);
            setPreviewDetail(null);
        }
    }, [filteredTests, previewTestKey]);

    // Auto-open popup for initialTestKey once workspace data has loaded
    const initialTestKeyOpenedRef = React.useRef(false);
    useEffect(() => {
        if (
            !initialTestKey ||
            initialTestKeyOpenedRef.current ||
            filteredTests.length === 0
        ) {
            return;
        }
        const match = filteredTests.find((tc) => tc.test_key === initialTestKey);
        if (match) {
            initialTestKeyOpenedRef.current = true;
            setPreviewTestKey(initialTestKey);
            void loadPreviewDetail(initialTestKey);
        }
    }, [filteredTests, initialTestKey, loadPreviewDetail]);

    useEffect(() => {
        if (!selectedJob || !canUseWorkspace) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            void loadWorkspace(false);
        }, selectedArtifact ? WORKSPACE_POLL_INTERVAL_MS : JOB_POLL_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [canUseWorkspace, loadWorkspace, selectedArtifact, selectedJob]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            void fetchRequirementJobs(projectId);
        }, JOB_POLL_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [fetchRequirementJobs, projectId]);

    useEffect(() => {
        if (!isAzureProject || !selectedJobProjectId || !selectedJobStableId || !selectedArtifactStableId) {
            return;
        }

        let active = true;
        fetchLatestRequirementAdoPushJob(selectedJobProjectId, selectedJobStableId, selectedArtifactStableId)
            .then((job) => {
                if (active && job && ['queued', 'running'].includes((job.status || '').toLowerCase())) {
                    setAdoPushJob(job);
                }
            })
            .catch(() => {
                // Best-effort recovery only; normal page loading should not be blocked.
            });

        return () => {
            active = false;
        };
    }, [isAzureProject, selectedArtifactStableId, selectedJobProjectId, selectedJobStableId]);

    useEffect(() => {
        if (!adoPushJob || !adoPushActive || !selectedJobProjectId || !selectedJobStableId || !selectedArtifactStableId) {
            return undefined;
        }

        let active = true;
        const poll = async () => {
            try {
                const next = await fetchRequirementAdoPushJob(
                    selectedJobProjectId,
                    selectedJobStableId,
                    selectedArtifactStableId,
                    adoPushJob.id,
                );
                if (!active) {
                    return;
                }
                setAdoPushJob(next);

                const statusValue = (next.status || '').toLowerCase();
                if (
                    !['queued', 'running'].includes(statusValue) &&
                    !finalizedAdoPushJobIdsRef.current.has(next.id)
                ) {
                    finalizedAdoPushJobIdsRef.current.add(next.id);
                    await loadWorkspace(false);
                    await refreshAdoBackedProjectState();
                    if (next.scope === 'all') {
                        try {
                            const comparison = await compareRequirementTestCasesWithAdo(
                                selectedJobProjectId,
                                selectedJobStableId,
                                selectedArtifactStableId,
                                {},
                            );
                            if (active) {
                                setAdoComparison(comparison);
                            }
                        } catch {
                            if (active) {
                                setAdoComparison(null);
                            }
                        }
                    }

                    if (active) {
                        const pushed = next.pushed_count || next.result_summary?.pushed_count || 0;
                        const total = next.total_count || 0;
                        const failed = next.failed_count || next.result_summary?.failures?.length || 0;
                        const structureMessages = next.result_summary?.ado_structure?.messages?.filter(Boolean) || [];
                        setActionAlert({
                            variant: statusValue === 'failed' ? 'danger' : failed > 0 ? 'info' : 'success',
                            text: statusValue === 'failed'
                                ? (next.error_message || 'Azure DevOps push failed.')
                                : `Pushed ${pushed} of ${total} test case${total === 1 ? '' : 's'} to Azure DevOps.${failed ? ` ${failed} item${failed === 1 ? '' : 's'} need attention.` : ''}${structureMessages.length ? ` ${structureMessages.join(' ')}` : ''}`,
                        });
                    }
                }
            } catch (error: any) {
                if (active) {
                    setActionAlert({
                        variant: 'danger',
                        text: error?.response?.data?.detail || error?.message || 'Failed to read Azure DevOps push progress.',
                    });
                }
            }
        };

        void poll();
        const intervalId = window.setInterval(() => {
            void poll();
        }, ADO_PUSH_POLL_INTERVAL_MS);

        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [adoPushActive, adoPushJob, loadWorkspace, refreshAdoBackedProjectState, selectedArtifactStableId, selectedJobProjectId, selectedJobStableId]);

    useEffect(() => {
        if (!isAzureProject || !selectedJobProjectId || !selectedJobStableId || !selectedArtifactStableId) {
            return;
        }

        let active = true;
        fetchLatestAdoCompareSyncJob(selectedJobProjectId, selectedJobStableId, selectedArtifactStableId)
            .then((job) => {
                if (!active || !job || !['queued', 'running'].includes((job.status || '').toLowerCase())) {
                    return;
                }
                setAdoCompareSyncJob(job);
                const requestedIds = job.result_summary?.requested_change_ids || [];
                const nextSyncingIds = new Set(requestedIds);
                syncingAdoChangeIdsRef.current = nextSyncingIds;
                setSyncingAdoChangeIds(nextSyncingIds);
            })
            .catch(() => {
                // Best-effort recovery only; normal page loading should not be blocked.
            });

        return () => {
            active = false;
        };
    }, [isAzureProject, selectedArtifactStableId, selectedJobProjectId, selectedJobStableId]);

    useEffect(() => {
        if (!adoCompareSyncJob || !adoCompareSyncActive || !selectedJobProjectId || !selectedJobStableId || !selectedArtifactStableId) {
            return undefined;
        }

        let active = true;
        const poll = async () => {
            try {
                const next = await fetchAdoCompareSyncJob(
                    selectedJobProjectId,
                    selectedJobStableId,
                    selectedArtifactStableId,
                    adoCompareSyncJob.id,
                );
                if (!active) {
                    return;
                }
                setAdoCompareSyncJob(next);

                const requestedIds = next.result_summary?.requested_change_ids || Array.from(syncingAdoChangeIdsRef.current);
                const statusValue = (next.status || '').toLowerCase();
                if (['queued', 'running'].includes(statusValue)) {
                    const nextSyncingIds = new Set(requestedIds);
                    syncingAdoChangeIdsRef.current = nextSyncingIds;
                    setSyncingAdoChangeIds(nextSyncingIds);
                    return;
                }

                if (!finalizedAdoCompareSyncJobIdsRef.current.has(next.id)) {
                    finalizedAdoCompareSyncJobIdsRef.current.add(next.id);
                    const remainingSyncingIds = new Set(syncingAdoChangeIdsRef.current);
                    requestedIds.forEach((changeId) => remainingSyncingIds.delete(changeId));
                    syncingAdoChangeIdsRef.current = remainingSyncingIds;
                    setSyncingAdoChangeIds(remainingSyncingIds);

                    const comparison = next.result_summary?.comparison;
                    if (comparison) {
                        setAdoComparison(comparison);
                    } else {
                        try {
                            const refreshedComparison = await compareRequirementTestCasesWithAdo(
                                selectedJobProjectId,
                                selectedJobStableId,
                                selectedArtifactStableId,
                                {},
                            );
                            if (active) {
                                setAdoComparison(refreshedComparison);
                            }
                        } catch {
                            // Keep existing comparison if refresh fails.
                        }
                    }
                    setIgnoredAdoChangeIds(new Set());
                    await loadWorkspace(false);
                    await refreshAdoBackedProjectState();
                    if (previewTestKey) {
                        void loadPreviewDetail(previewTestKey, false);
                    }

                    if (active) {
                        setActionAlert(null);
                    }
                }
            } catch (error: any) {
                if (active) {
                    setActionAlert({
                        variant: 'danger',
                        text: error?.response?.data?.detail || error?.message || 'Failed to read Azure DevOps sync progress.',
                    });
                }
            }
        };

        void poll();
        const intervalId = window.setInterval(() => {
            void poll();
        }, ADO_COMPARE_SYNC_POLL_INTERVAL_MS);

        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [adoCompareSyncActive, adoCompareSyncJob, loadPreviewDetail, loadWorkspace, previewTestKey, refreshAdoBackedProjectState, selectedArtifactStableId, selectedJobProjectId, selectedJobStableId]);

    const handleDownloadArtifact = useCallback(async () => {
        if (!selectedJob || !selectedArtifact) {
            return;
        }

        try {
            const downloaded = await downloadRequirementArtifact(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                projectArtifactDownloadFilename(
                    resolvedProjectName,
                    selectedArtifact.artifact_type,
                    selectedArtifact.display_name || 'artifact.json',
                ),
            );
            triggerDownloadFromBlob(downloaded.blob, downloaded.filename);
        } catch (error: any) {
            setPageError(error?.response?.data?.detail || error?.message || 'Failed to download the test artifact.');
        }
    }, [downloadRequirementArtifact, selectedArtifact, selectedJob]);

    const handleGenerateGherkin = useCallback(async () => {
        if (!selectedJob || !selectedArtifact) {
            return;
        }

        setGherkinLoading(true);
        setActionAlert(null);

        try {
            const response = await generateGherkin(selectedJob.project_id, selectedJob.id, selectedArtifact.id);
            navigate(`/projects/${projectId}?tab=gherkin&jobId=${selectedJob.id}&gherkinJobId=${response.gherkin_job_id}`);
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.message || 'Failed to start Gherkin generation.';
            if (error?.response?.status === 409) {
                navigate(`/projects/${projectId}?tab=gherkin&jobId=${selectedJob.id}&trackGherkin=1`);
                return;
            }
            setActionAlert({ variant: 'danger', text: detail });
        } finally {
            setGherkinLoading(false);
        }
    }, [navigate, projectId, selectedArtifact, selectedJob]);

    const handleToggleTestSelection = useCallback((testKey: string) => {
        setSelectedTestKeys((previous) => {
            const next = new Set(previous);
            if (next.has(testKey)) {
                next.delete(testKey);
            } else {
                next.add(testKey);
            }
            return next;
        });
    }, []);

    const handleToggleVisibleSelection = useCallback(() => {
        setSelectedTestKeys((previous) => {
            const next = new Set(previous);
            const shouldClearVisible = filteredTests.length > 0 && filteredTests.every((testCase) => next.has(testCase.test_key));

            filteredTests.forEach((testCase) => {
                if (shouldClearVisible) {
                    next.delete(testCase.test_key);
                } else {
                    next.add(testCase.test_key);
                }
            });

            return next;
        });
    }, [filteredTests]);

    const handleClearSelection = useCallback(() => {
        setSelectedTestKeys((previous) => (previous.size === 0 ? previous : new Set()));
    }, []);

    const handlePushToAdo = useCallback(async (scope: 'selected' | 'all') => {
        if (!selectedJob || !selectedArtifact || !workspace) {
            return;
        }

        const testKeys = scope === 'all'
            ? workspace.tests.map((testCase) => testCase.test_key)
            : Array.from(selectedTestKeys);

        if (testKeys.length === 0) {
            setActionAlert({ variant: 'danger', text: 'Select at least one test case before pushing to Azure DevOps.' });
            return;
        }

        setActionAlert(null);

        try {
            const response = await startRequirementTestCasesAdoPush(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                { test_keys: testKeys, prune_stale_suites: scope === 'all' },
            );
            const pushJob = await fetchRequirementAdoPushJob(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                response.push_job_id,
            );
            setAdoPushJob(pushJob);
            if (!['queued', 'running'].includes((pushJob.status || '').toLowerCase())) {
                await refreshAdoBackedProjectState();
            }
            setActionAlert({
                variant: 'info',
                text: `Azure DevOps push started for ${response.total_count} test case${response.total_count === 1 ? '' : 's'}. You can keep using the app while it runs.`,
            });
            setIgnoredAdoChangeIds(new Set());
        } catch (error: any) {
            setActionAlert({
                variant: 'danger',
                text: getAdoPushErrorMessage(error),
            });
        }
    }, [refreshAdoBackedProjectState, selectedArtifact, selectedJob, selectedTestKeys, workspace]);

    const handleCompareWithAdo = useCallback(async () => {
        if (!selectedJob || !selectedArtifact || !workspace) {
            return;
        }

        setAdoCompareLoading(true);
        setActionAlert(null);
        setIgnoredAdoChangeIds(new Set());

        try {
            const response = await compareRequirementTestCasesWithAdo(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                {},
            );
            setAdoComparison(response);
            setActionAlert({
                variant: response.change_count === 0 ? 'success' : 'info',
                text: response.change_count === 0
                    ? 'Azure DevOps is aligned with this test workspace.'
                    : `Found ${response.change_count} Azure DevOps difference${response.change_count === 1 ? '' : 's'} to review.`,
            });
        } catch (error: any) {
            setActionAlert({
                variant: 'danger',
                text: error?.response?.data?.detail || error?.message || 'Failed to compare with Azure DevOps.',
            });
        } finally {
            setAdoCompareLoading(false);
        }
    }, [selectedArtifact, selectedJob, workspace]);

    const handleSyncAdoChanges = useCallback(async (changes: AdoCompareChange[], requestedScope?: 'row' | 'group' | 'all') => {
        if (!selectedJob || !selectedArtifact) {
            return;
        }
        if (adoCompareSyncActive) {
            setActionAlert({
                variant: 'info',
                text: 'Azure DevOps sync is already running. Wait for it to finish before starting another sync.',
            });
            return;
        }

        const activeChangeIds = syncingAdoChangeIdsRef.current;
        const syncableChanges = changes.filter((change) => !activeChangeIds.has(change.id));
        const changeIds = Array.from(new Set(syncableChanges.map((change) => change.id).filter(Boolean)));
        if (changeIds.length === 0) {
            return;
        }

        const nextSyncingIds = new Set(activeChangeIds);
        changeIds.forEach((changeId) => nextSyncingIds.add(changeId));
        syncingAdoChangeIdsRef.current = nextSyncingIds;
        setSyncingAdoChangeIds(nextSyncingIds);
        setActionAlert(null);

        try {
            const scope = requestedScope || (changeIds.length === 1 ? 'row' : changeIds.length === syncableVisibleAdoChanges.length ? 'all' : 'group');
            const response = await startAdoCompareChangesSync(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                changeIds,
                scope,
            );
            const syncJob = await fetchAdoCompareSyncJob(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                response.sync_job_id,
            );
            setAdoCompareSyncJob(syncJob);
            if (!['queued', 'running'].includes((syncJob.status || '').toLowerCase())) {
                const remainingSyncingIds = new Set(syncingAdoChangeIdsRef.current);
                changeIds.forEach((changeId) => remainingSyncingIds.delete(changeId));
                syncingAdoChangeIdsRef.current = remainingSyncingIds;
                setSyncingAdoChangeIds(remainingSyncingIds);
                await refreshAdoBackedProjectState();
            }
        } catch (error: any) {
            const remainingSyncingIds = new Set(syncingAdoChangeIdsRef.current);
            changeIds.forEach((changeId) => remainingSyncingIds.delete(changeId));
            syncingAdoChangeIdsRef.current = remainingSyncingIds;
            setSyncingAdoChangeIds(remainingSyncingIds);
            setActionAlert({
                variant: 'danger',
                text: error?.response?.data?.detail || error?.message || 'Failed to sync Azure DevOps change.',
            });
        }
    }, [adoCompareSyncActive, refreshAdoBackedProjectState, selectedArtifact, selectedJob, syncableVisibleAdoChanges.length]);

    const handleIgnoreAdoChange = useCallback((changeId: string) => {
        setIgnoredAdoChangeIds((previous) => {
            const next = new Set(previous);
            next.add(changeId);
            return next;
        });
    }, []);

    const handleExport = useCallback((format: TestCaseExportFormat) => {
        if (exportTests.length === 0) {
            setPageError('No test cases are available to export.');
            return;
        }

        try {
            setPageError(null);
            const exportPayload = buildTestCaseCsvExport(exportTests, {
                format,
                projectName: resolvedProjectName,
            });
            const blob = new Blob([exportPayload.csv], { type: 'text/csv;charset=utf-8' });
            triggerDownloadFromBlob(blob, exportPayload.filename);
        } catch (error: any) {
            setPageError(error?.message || 'Failed to export the selected test cases.');
        }
    }, [exportTests, resolvedProjectName]);

    const handleRunTests = useCallback(() => {
        if (!selectedArtifact) {
            setActionAlert({ variant: 'info', text: 'Test execution is available after the final test-case artifact is created.' });
            return;
        }

        const selectedKeys = Array.from(selectedTestKeys).filter((testKey) => testKey.trim().length > 0);
        const selectedPath = selectedFolder?.path || [];
        const isRootFolder = selectedPath.length <= 1;
        const state = selectedKeys.length > 0
            ? {
                source: 'test-cases',
                scope: 'tests',
                generationId: selectedJob?.id ?? null,
                artifactId: selectedArtifact?.id ?? null,
                testKeys: selectedKeys,
            }
            : !isRootFolder && selectedPath.length > 0
                ? {
                    source: 'test-cases',
                    scope: 'folder',
                    generationId: selectedJob?.id ?? null,
                    artifactId: selectedArtifact?.id ?? null,
                    folderPath: selectedPath,
                }
            : undefined;

        navigate(`/projects/${projectId}/automated-tests`, state ? { state } : undefined);
    }, [navigate, projectId, selectedArtifact, selectedFolder?.path, selectedJob?.id, selectedTestKeys]);

    const handleOpenMoveDialog = useCallback((testCase: RequirementWorkspaceTestCase) => {
        const selectedMoveTests = selectedTestKeys.has(testCase.test_key)
            ? (workspace?.tests || []).filter((candidate) => selectedTestKeys.has(candidate.test_key))
            : [];
        const testsToMove = selectedMoveTests.length > 1 ? selectedMoveTests : [testCase];
        const commonFolderPath = testsToMove.length > 0 && testsToMove.every(
            (candidate) => (candidate.folder_path || []).join('/') === (testsToMove[0].folder_path || []).join('/'),
        )
            ? formatFolderPathForDisplay(testsToMove[0].folder_path || [])
            : '';

        setMoveDialog({
            open: true,
            test: testCase,
            tests: testsToMove,
            value: commonFolderPath,
            saving: false,
            error: null,
        });
    }, [selectedTestKeys, workspace?.tests]);

    const handleSaveMoveDialog = useCallback(async () => {
        const testsToMove = moveDialog.tests?.length ? moveDialog.tests : (moveDialog.test ? [moveDialog.test] : []);
        if (!selectedJob || !selectedArtifact || testsToMove.length === 0) {
            return;
        }

        const parsedFolder = normalizeFolderPathInput(moveDialog.value);
        if (parsedFolder.length === 0) {
            setMoveDialog((previous) => ({ ...previous, error: 'Provide a folder path such as All Test Cases / Regression / Authentication.' }));
            return;
        }

        setMoveDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            for (const testCase of testsToMove) {
                await updateRequirementTestFolder(
                    selectedJob.project_id,
                    selectedJob.id,
                    selectedArtifact.id,
                    testCase.test_key,
                    parsedFolder,
                );
            }
            setSelectedTestKeys((previous) => {
                if (previous.size === 0) {
                    return previous;
                }
                const movedKeys = new Set(testsToMove.map((testCase) => testCase.test_key));
                const next = new Set(Array.from(previous).filter((testKey) => !movedKeys.has(testKey)));
                return next.size === previous.size ? previous : next;
            });
            setMoveDialog({ open: false, test: null, tests: [], value: '', saving: false, error: null });
            await loadWorkspace(false);
        } catch (error: any) {
            setMoveDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to update the folder path.',
            }));
        }
    }, [loadWorkspace, moveDialog.test, moveDialog.tests, moveDialog.value, selectedArtifact, selectedJob]);

    const handleOpenRenameDialog = useCallback((folder: RequirementTestFolderNode) => {
        if ((folder.path || []).length <= 1) {
            return;
        }
        setRenameDialog({
            open: true,
            folder,
            value: folder.name || '',
            saving: false,
            error: null,
        });
    }, []);

    const handleSaveRenameDialog = useCallback(async () => {
        if (!selectedJob || !selectedArtifact || !renameDialog.folder) {
            return;
        }

        const newName = renameDialog.value.trim();
        if (!newName) {
            setRenameDialog((previous) => ({ ...previous, error: 'Provide a folder name.' }));
            return;
        }

        setRenameDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            const response = await renameRequirementTestFolder(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                renameDialog.folder.path || [],
                newName,
            );
            setRenameDialog({ open: false, folder: null, value: '', saving: false, error: null });
            await loadWorkspace(false);
            setSelectedFolderId(response.folder_id);
        } catch (error: any) {
            setRenameDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to rename the folder.',
            }));
        }
    }, [loadWorkspace, renameDialog.folder, renameDialog.value, selectedArtifact, selectedJob]);

    const handleOpenDeleteTestDialog = useCallback((testCase: RequirementWorkspaceTestCase) => {
        setDeleteTestDialog({ open: true, test: testCase, saving: false, error: null });
    }, []);

    const handleConfirmDeleteTestDialog = useCallback(async () => {
        if (!selectedJob || !selectedArtifact || !deleteTestDialog.test) {
            return;
        }
        const testKey = deleteTestDialog.test.test_key;
        setDeleteTestDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            await deleteRequirementTestCase(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                testKey,
            );
            setSelectedTestKeys((previous) => {
                if (!previous.has(testKey)) {
                    return previous;
                }
                const next = new Set(previous);
                next.delete(testKey);
                return next;
            });
            if (previewTestKey === testKey) {
                setPreviewTestKey(null);
                setPreviewDetail(null);
                setPreviewDetailError(null);
            }
            setDeleteTestDialog({ open: false, test: null, saving: false, error: null });
            await loadWorkspace(false);
        } catch (error: any) {
            setDeleteTestDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to delete the test case.',
            }));
        }
    }, [deleteTestDialog.test, loadWorkspace, previewTestKey, selectedArtifact, selectedJob]);

    const handleOpenDeleteFolderDialog = useCallback((folder: RequirementTestFolderNode) => {
        if ((folder.path || []).length <= 1) {
            return;
        }
        setDeleteFolderDialog({ open: true, folder, saving: false, error: null });
    }, []);

    const handleConfirmDeleteFolderDialog = useCallback(async () => {
        if (!selectedJob || !selectedArtifact || !deleteFolderDialog.folder) {
            return;
        }
        const folder = deleteFolderDialog.folder;
        const parentFolderId = (folder.path || []).slice(0, -1).join('/') || 'Results';
        setDeleteFolderDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            const response = await deleteRequirementTestFolder(
                selectedJob.project_id,
                selectedJob.id,
                selectedArtifact.id,
                folder.path || [],
            );
            const deletedKeys = new Set(response.deleted_test_keys || []);
            setSelectedTestKeys((previous) => {
                if (previous.size === 0) {
                    return previous;
                }
                const next = new Set(Array.from(previous).filter((testKey) => !deletedKeys.has(testKey)));
                return next.size === previous.size ? previous : next;
            });
            if (previewTestKey && deletedKeys.has(previewTestKey)) {
                setPreviewTestKey(null);
                setPreviewDetail(null);
                setPreviewDetailError(null);
            }
            setDeleteFolderDialog({ open: false, folder: null, saving: false, error: null });
            await loadWorkspace(false);
            setSelectedFolderId(parentFolderId);
        } catch (error: any) {
            setDeleteFolderDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to delete the folder.',
            }));
        }
    }, [deleteFolderDialog.folder, loadWorkspace, previewTestKey, selectedArtifact, selectedJob]);

    // Feature 7: popup handlers
    const handlePreviewTest = useCallback(
        (testCase: RequirementWorkspaceTestCase) => {
            setPreviewTestKey(testCase.test_key);
            setPreviewDetail(null);
            setPreviewDetailError(null);
            void loadPreviewDetail(testCase.test_key);
        },
        [loadPreviewDetail],
    );

    const handleClosePreview = useCallback(() => {
        setPreviewTestKey(null);
        setPreviewDetail(null);
        setPreviewDetailError(null);
    }, []);

    const handleTestDeleted = useCallback(
        (testKey: string) => {
            setPreviewTestKey(null);
            setPreviewDetail(null);
            setSelectedTestKeys((previous) => {
                if (!previous.has(testKey)) {
                    return previous;
                }
                const next = new Set(previous);
                next.delete(testKey);
                return next;
            });
            // Refresh workspace so the deleted test disappears from the list
            void loadWorkspace(false);
        },
        [loadWorkspace],
    );

    const handleTestEdited = useCallback(
        (testKey: string, updatedTestCase: Record<string, any>) => {
            // Immediately patch previewDetail so popup re-renders with no delay
            setPreviewDetail((prev) => {
                if (!prev) return prev;
                return { ...prev, test: { ...prev.test, ...updatedTestCase } };
            });
            // Also refresh workspace card list (title may have changed)
            void loadWorkspace(false);
        },
        [loadWorkspace],
    );

    const handleTestCaseCreated = useCallback(
        (_newTestCase: Record<string, any>) => {
            setShowAddTestCaseModal(false);
            void loadWorkspace(false);
        },
        [loadWorkspace],
    );

    const popupChatContext = useMemo(() => {
        if (!previewDetail || !selectedJob || !selectedArtifact) {
            return null;
        }
        return {
            context_mode: previewDetail.latest_run?.run_id ? 'run' : 'test',
            generation_id: selectedJob.id,
            artifact_id: selectedArtifact.id,
            test_case_id: previewDetail.test.id ?? previewDetail.test.test_case_id ?? null,
            test_key: previewDetail.test.test_key,
            run_id: previewDetail.latest_run?.run_id ?? null,
        } as const;
    }, [previewDetail, selectedArtifact, selectedJob]);

    const popupChat = previewDetail && selectedJob && selectedArtifact && popupChatContext ? (
        <ProjectChatPanel
            key={`${previewDetail.test.test_key}:${previewDetail.latest_run?.run_id || 'test'}`}
            projectId={projectId}
            variant="embedded"
            title="Test Case Analysis"
            subtitle={previewDetail.latest_run?.run_id ? 'Scoped to the selected test and latest run.' : 'Scoped to the selected test.'}
            context={popupChatContext}
            allowMaximize
        />
    ) : null;

    if (!Number.isFinite(projectId)) {
        return (
            <div className="rrw-alert-wrap">
                <Alert variant="danger">Invalid project identifier.</Alert>
            </div>
        );
    }

    if (pageLoading) {
        return (
            <div className="rrw-loading">
                <Spinner animation="border" />
            </div>
        );
    }

    if (pageError && workspaceJobs.length === 0) {
        return (
            <div className="rrw-alert-wrap">
                <Alert variant="danger">{pageError}</Alert>
            </div>
        );
    }

    if (workspaceJobs.length === 0) {
        return (
            <div className="rrw-alert-wrap">
                <Alert variant="warning">
                    No requirement generation jobs with test results are available for this project yet. Upload requirement documents and queue Test Case Creation to populate this workspace.
                </Alert>
            </div>
        );
    }

    return (
        <div className={`rrw-page ${variant === 'embedded' ? 'rrw-page-embedded' : ''}`}>
            {topContent}

            <div className={`rrw-hero ${variant === 'embedded' ? 'rrw-hero-embedded' : ''}`}>
                <div>
                    <p className="rrw-hero-kicker">{variant === 'embedded' ? 'Project test cases' : 'Test Results Workspace'}</p>
                    <h1 className="rrw-hero-title">{resolvedProjectName}</h1>
                    <p className="rrw-hero-copy">
                        Browse auto-classified tests, review evidence and run history, and launch focused Playwright runs without leaving the workspace.
                    </p>
                </div>

                <div className="rrw-hero-actions">
                    <Dropdown align="end">
                        <Dropdown.Toggle variant="outline-secondary" size="sm" disabled={exportTests.length === 0}>
                            <Download size={12} className="me-1" />
                            Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="rrw-export-menu">
                            <Dropdown.Header>
                                {selectedTests.length > 0
                                    ? `Export ${selectedTests.length} selected test${selectedTests.length === 1 ? '' : 's'}`
                                    : `Export all ${workspace?.tests.length ?? 0} tests`}
                            </Dropdown.Header>
                            <Dropdown.Item onClick={() => handleExport('ado')}>ADO CSV</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleExport('jira')}>Jira CSV</Dropdown.Item>
                            <Dropdown.ItemText className="rrw-export-menu-note">
                                Checked test cases are exported when any are selected. Otherwise, the full loaded catalog is exported.
                            </Dropdown.ItemText>
                        </Dropdown.Menu>
                    </Dropdown>
                    <Button variant="outline-secondary" size="sm" onClick={handleDownloadArtifact} disabled={!selectedArtifact}>
                        <Download size={12} className="me-1" />
                        Download Tests
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleRunTests}
                        disabled={!selectedArtifact}
                    >
                        <PlayFill size={12} className="me-1" />
                        Run Tests
                    </Button>
                    <Button
                        variant="outline-light"
                        size="sm"
                        onClick={() => void handleGenerateGherkin()}
                        disabled={!selectedArtifact || gherkinLoading}
                    >
                        {gherkinLoading ? (
                            <Spinner size="sm" className="me-1" />
                        ) : (
                            <FileEarmarkCode size={12} className="me-1" />
                        )}
                        Generate Gherkin Scripts
                    </Button>
                </div>
            </div>

            {pageError && <Alert variant="danger">{pageError}</Alert>}
            {workspaceError && <Alert variant="warning">{workspaceError}</Alert>}
            {actionAlert && <Alert variant={actionAlert.variant}>{actionAlert.text}</Alert>}
            {adoPushJob && (
                <div className={`rrw-ado-push-progress rrw-ado-push-progress-${(adoPushJob.status || 'default').toLowerCase()}`}>
                    <div className="rrw-ado-push-progress-main">
                        <div>
                            <div className="rrw-ado-push-progress-title">
                                Azure DevOps push {adoPushActive ? 'in progress' : adoPushJob.status}
                            </div>
                            <div className="rrw-ado-push-progress-subtitle">
                                {adoPushJob.message || 'Preparing Azure DevOps push...'}
                                {adoPushCurrentLabel ? ` Current: ${adoPushCurrentLabel}` : ''}
                            </div>
                        </div>
                        <div className="rrw-ado-push-progress-count">
                            {adoPushJob.processed_count || 0} / {adoPushJob.total_count || 0}
                        </div>
                    </div>
                    <div className="rrw-ado-push-progress-track">
                        <div className="rrw-ado-push-progress-fill" style={{ width: `${adoPushProgressPercent}%` }} />
                    </div>
                    <div className="rrw-ado-push-progress-meta">
                        <span>{adoPushJob.pushed_count || 0} pushed</span>
                        <span>{adoPushJob.failed_count || 0} failed</span>
                        {adoPushJob.error_message ? <span>{adoPushJob.error_message}</span> : null}
                        {adoPushStructureMessages.map((message) => (
                            <span key={message}>{message}</span>
                        ))}
                    </div>
                </div>
            )}
            {selectedJob && isLiveWorkspace && (
                <Alert variant="info">
                    {isActiveGenerationStatus(selectedJob.status) && (
                        <Spinner animation="border" size="sm" className="me-2" />
                    )}
                    Generation {liveVersionLabel} is {selectedJob.status}.{' '}
                    {liveResults && liveResults.total_story_count > 0 ? (
                        <>
                            Saved <strong>{liveResults.completed_story_count}</strong> of{' '}
                            <strong>{liveResults.total_story_count}</strong> user{' '}
                            {liveResults.total_story_count === 1 ? 'story' : 'stories'} ·{' '}
                            <strong>{liveResults.test_case_count}</strong> test{' '}
                            {liveResults.test_case_count === 1 ? 'case' : 'cases'} so far. New cases appear here as each user story is saved.
                        </>
                    ) : (
                        'Showing live saved test cases from the latest database checkpoint.'
                    )}
                </Alert>
            )}
            {selectedJob && !selectedArtifact && !isLiveWorkspace && (
                <Alert variant={selectedJob.status === 'failed' ? 'danger' : 'info'}>
                    {isActiveGenerationStatus(selectedJob.status) ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Generation {liveVersionLabel} is {selectedJob.status}.{' '}
                            {isRequirementsPhase
                                ? 'Analyzing requirements and building the user-story tree — test cases start appearing here per user story once this phase completes.'
                                : 'Generating test cases — the first user story will appear here shortly, then the list grows as each story is saved.'}
                        </>
                    ) : (
                        'This generation does not have a test-case artifact yet.'
                    )}
                </Alert>
            )}

            <div className="rrw-metrics">
                <div className="rrw-metric-card">
                    <div className="rrw-metric-label">Generated Catalog</div>
                    <div className="rrw-metric-value">
                        {selectedJob?.version_number ? `v${selectedJob.version_number}` : 'Latest'}
                    </div>
                    <div className="rrw-metric-note">
                        {selectedJob?.created_at ? formatLocalDateTimeFromUTC(selectedJob.created_at) : 'No timestamp'}
                        {selectedJob?.status ? ` • ${selectedJob.status}` : ''}
                    </div>
                    {isLiveWorkspace && (
                        <div className="rrw-metric-note">
                            {liveResults && liveResults.total_story_count > 0
                                ? `Live · user story ${liveResults.completed_story_count}/${liveResults.total_story_count}`
                                : 'Live partial results'}
                        </div>
                    )}
                </div>
                <div className="rrw-metric-card">
                    <div className="rrw-metric-label">Total Tests</div>
                    <div className="rrw-metric-value">{workspace?.summary.total ?? 0}</div>
                    <div className="rrw-metric-note">Across all folders</div>
                </div>
                <div className="rrw-metric-card">
                    <div className="rrw-metric-label">Latest Run</div>
                    <div className="rrw-metric-value">
                        {workspace?.summary.latest_run_at ? formatLocalDateTimeFromUTC(workspace.summary.latest_run_at) : 'Never'}
                    </div>
                    <div className="rrw-metric-note">Most recent execution</div>
                </div>
                {isAzureProject && (
                    <div className="rrw-metric-card">
                        <div className="rrw-metric-label">Not in ADO</div>
                        <div className="rrw-metric-value">{unsyncedCount}</div>
                        <div className="rrw-metric-note">{unsyncedCount === 0 ? 'All synced' : 'Awaiting push'}</div>
                    </div>
                )}
            </div>

            <div className={`rrw-workspace ${showJobSelector ? '' : 'rrw-workspace--focused'}`}>
                <div className="rrw-column rrw-column-directory">
                    {showJobSelector && (
                        <div className="rrw-panel rrw-jobs-panel">
                            <div className="rrw-panel-header">
                                <div>
                                    <h3 className="rrw-panel-title">Generation Jobs</h3>
                                    <p className="rrw-panel-subtitle">Select the generated test snapshot you want to inspect or push.</p>
                                </div>
                            </div>

                            <div className="rrw-job-list">
                                {workspaceJobs.map((job) => {
                                    const isActive = job.id === effectiveSelectedJobId;
                                    const hasTests = !!getTestArtifact(job);
                                    const hasLiveTests = Boolean(job.live_results?.test_case_count);
                                    const versionLabel = job.version_number ? `v${job.version_number}` : `#${job.id}`;
                                    return (
                                        <button
                                            key={job.id}
                                            type="button"
                                            className={`rrw-job-item ${isActive ? 'rrw-job-item-active' : ''}`}
                                            onClick={() => setSelectedJob(job.id)}
                                        >
                                            <div className="rrw-job-item-header">
                                                <span>Test Snapshot {versionLabel}</span>
                                                <span className={`rrw-status-pill rrw-status-pill-${(job.status || 'default').toLowerCase()}`}>{job.status}</span>
                                            </div>
                                            <div className="rrw-job-item-meta">
                                                <span>{formatLocalDateTimeFromUTC(job.created_at)}</span>
                                                <span>
                                                    {hasTests
                                                        ? `${job.artifact_counts?.test_cases || 0} tests`
                                                        : hasLiveTests
                                                            ? `${job.live_results?.test_case_count || 0} live tests`
                                                            : 'Preparing test cases'}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="rrw-panel rrw-directory-panel">
                        <div className="rrw-panel-header">
                            <div>
                                <h3 className="rrw-panel-title">Test Directory</h3>
                                <p className="rrw-panel-subtitle">Auto-classified folders with live result counts.</p>
                            </div>
                        </div>

                        {workspaceLoading && !workspace ? (
                            <div className="rrw-empty-panel"><Spinner animation="border" size="sm" /></div>
                        ) : !canUseWorkspace ? (
                            <div className="rrw-empty-panel">Waiting for generated test cases.</div>
                        ) : (
                            <TestDirectoryTree
                                directory={workspace?.directory}
                                selectedFolderId={selectedFolder?.id || workspace?.default_folder_id}
                                highlightedFolderIds={highlightedFolderIds}
                                onSelectFolder={(folder) => setSelectedFolderId(folder.id)}
                                onRenameFolder={handleOpenRenameDialog}
                                onDeleteFolder={handleOpenDeleteFolderDialog}
                            />
                        )}
                    </div>
                </div>

                <div className="rrw-column rrw-column-results">
                    {isAzureProject && (
                        <div className="rrw-panel rrw-ado-compare-panel">
                            <div className="rrw-panel-header rrw-ado-compare-header">
                                <div>
                                    <h3 className="rrw-panel-title">Azure DevOps Alignment</h3>
                                    <p className="rrw-panel-subtitle">Review work item, suite, and membership differences before syncing.</p>
                                </div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => void handleCompareWithAdo()}
                                    disabled={!selectedArtifact || adoCompareLoading}
                                >
                                    {adoCompareLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                    Compare with ADO
                                </Button>
                            </div>

                            {adoComparison && (
                                <div className="rrw-ado-compare-body">
                                    <div className="rrw-ado-compare-summary">
                                        <div className="rrw-ado-compare-summary-meta">
                                            <span>{visibleAdoChanges.length} visible change{visibleAdoChanges.length === 1 ? '' : 's'}</span>
                                            {adoComparison.test_plan_id ? <span>Test Plan {adoComparison.test_plan_id}</span> : <span>No Test Plan mapped</span>}
                                        </div>
                                        {visibleAdoChanges.length > 0 ? (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => void handleSyncAdoChanges(syncableVisibleAdoChanges, 'all')}
                                                disabled={!hasSyncableVisibleAdoChanges || adoCompareSyncActive}
                                            >
                                                {allVisibleAdoChangesSyncing ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                                Sync All Changes
                                            </Button>
                                        ) : null}
                                    </div>

                                    {adoCompareSyncJob ? (
                                        <div className={`rrw-ado-sync-progress rrw-ado-sync-progress-${(adoCompareSyncJob.status || 'default').toLowerCase()}`}>
                                            <div className="rrw-ado-sync-progress-main">
                                                <div>
                                                    <div className="rrw-ado-sync-progress-title">
                                                        Azure DevOps sync {adoCompareSyncActive ? 'in progress' : adoCompareSyncJob.status}
                                                    </div>
                                                    <div className="rrw-ado-sync-progress-subtitle">
                                                        {adoCompareSyncJob.message || 'Preparing Azure DevOps sync...'}
                                                        {adoCompareSyncCurrentLabel ? ` Current: ${adoCompareSyncCurrentLabel}` : ''}
                                                    </div>
                                                </div>
                                                <div className="rrw-ado-sync-progress-count">
                                                    {adoCompareSyncJob.processed_count || 0} / {adoCompareSyncJob.total_count || 0}
                                                </div>
                                            </div>
                                            <div className="rrw-ado-sync-progress-track">
                                                <div className="rrw-ado-sync-progress-fill" style={{ width: `${adoCompareSyncProgressPercent}%` }} />
                                            </div>
                                            <div className="rrw-ado-sync-progress-meta">
                                                <span>{adoCompareSyncJob.synced_count || 0} synced</span>
                                                <span>{adoCompareSyncJob.skipped_count || 0} skipped</span>
                                                <span>{adoCompareSyncJob.failed_count || 0} failed</span>
                                                {adoCompareSyncJob.error_message ? <span>{adoCompareSyncJob.error_message}</span> : null}
                                            </div>
                                        </div>
                                    ) : null}

                                    {visibleAdoChanges.length === 0 ? (
                                        <div className="rrw-ado-empty">No Azure DevOps differences to sync.</div>
                                    ) : (
                                        <div className="rrw-ado-change-groups">
                                            {adoChangesByGroup.map(([group, changes]) => {
                                                const syncableGroupChanges = changes;
                                                const hasSyncableGroupChanges = syncableGroupChanges.some((change) => !syncingAdoChangeIds.has(change.id));
                                                const allGroupChangesSyncing = syncableGroupChanges.length > 0 && syncableGroupChanges.every((change) => syncingAdoChangeIds.has(change.id));

                                                return (
                                                <div className="rrw-ado-change-group" key={group}>
                                                    <div className="rrw-ado-group-header">
                                                        <div className="rrw-ado-group-title">{group}</div>
                                                        {adoChangesByGroup.length > 1 ? (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => void handleSyncAdoChanges(syncableGroupChanges, 'group')}
                                                                disabled={!hasSyncableGroupChanges || adoCompareSyncActive}
                                                            >
                                                                {allGroupChangesSyncing ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                                                Sync This Group
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                    {changes.map((change) => {
                                                        const isSyncingChange = syncingAdoChangeIds.has(change.id);
                                                        const presentation = presentAdoCompareChange(change, isSyncingChange);

                                                        return (
                                                            <div className="rrw-ado-change-row" key={change.id}>
                                                                <div className="rrw-ado-change-main">
                                                                    <div className="rrw-ado-change-type">{presentation.title}</div>
                                                                    {presentation.meta.length > 0 ? (
                                                                        <div className="rrw-ado-change-meta">
                                                                            {presentation.meta.map((item) => <span key={item}>{item}</span>)}
                                                                        </div>
                                                                    ) : null}
                                                                    <div className="rrw-ado-change-values">
                                                                        {presentation.values.map((item) => <span key={item}>{item}</span>)}
                                                                    </div>
                                                                </div>
                                                                <div className="rrw-ado-change-actions">
                                                                    <Button
                                                                        variant={presentation.primaryActionVariant}
                                                                        size="sm"
                                                                        onClick={() => void handleSyncAdoChanges([change], 'row')}
                                                                        disabled={presentation.primaryActionDisabled || adoCompareSyncActive}
                                                                    >
                                                                        {isSyncingChange ? <Spinner animation="border" size="sm" /> : presentation.primaryActionLabel}
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        onClick={() => handleIgnoreAdoChange(change.id)}
                                                                        disabled={isSyncingChange || adoCompareSyncActive}
                                                                    >
                                                                        Ignore
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <TestResultsList
                        tests={filteredTests}
                        totalTestCount={workspace?.tests.length ?? 0}
                        selectedFolderName={selectedFolder?.name}
                        selectedTestKey={previewTestKey}
                        selectedTestKeys={selectedTestKeys}
                        selectedCount={selectedTests.length}
                        allVisibleSelected={allVisibleSelected}
                        search={search}
                        statusFilter={statusFilter}
                        lastRunFilter={lastRunFilter}
                        sortBy={sortBy}
                        onSearchChange={setSearch}
                        onStatusFilterChange={setStatusFilter}
                        onLastRunFilterChange={setLastRunFilter}
                        onSortChange={setSortBy}
                        onSelectTest={(testCase) => {
                            // selection is now handled by Preview button (Feature 7)
                        }}
                        onToggleTestSelection={(testCase) => handleToggleTestSelection(testCase.test_key)}
                        onToggleVisibleSelection={handleToggleVisibleSelection}
                        onClearSelection={handleClearSelection}
                        onMoveTest={selectedArtifact ? handleOpenMoveDialog : undefined}
                        onPreviewTest={selectedArtifact ? handlePreviewTest : undefined}
                        onRunTests={selectedArtifact ? handleRunTests : undefined}
                        onDeleteTest={selectedArtifact ? handleOpenDeleteTestDialog : undefined}
                        onAddTestCase={selectedJob && selectedArtifact ? () => setShowAddTestCaseModal(true) : undefined}
                        showAdoActions={isAzureProject && Boolean(selectedArtifact)}
                        adoPushInProgress={adoPushInProgress}
                        adoStatusOverrides={adoStatusOverrides}
                        onPushSelectedToAdo={selectedArtifact ? () => void handlePushToAdo('selected') : undefined}
                        onPushAllToAdo={selectedArtifact ? () => void handlePushToAdo('all') : undefined}
                    />
                </div>
            </div>

            {/* Feature 7: Test case popup modal */}
            {previewTestKey && selectedJob && selectedArtifact && (
                <TestCasePopup
                    testKey={previewTestKey}
                    projectId={projectId}
                    generationId={selectedJob.id}
                    artifactId={selectedArtifact.id}
                    detail={previewDetail}
                    detailLoading={previewLoading}
                    detailError={previewDetailError}
                    onClose={handleClosePreview}
                    onDeleted={handleTestDeleted}
                    onEdited={handleTestEdited}
                    chatPanel={popupChat}
                    adoTenant={workspace?.ado_tenant}
                    adoProjectName={workspace?.ado_project_name}
                />
            )}

            {/* Feature 7: Add test case modal */}
            {showAddTestCaseModal && selectedJob && selectedArtifact && (
                <AddTestCaseModal
                    projectId={projectId}
                    generationId={selectedJob.id}
                    artifactId={selectedArtifact.id}
                    directory={workspace?.directory}
                    onClose={() => setShowAddTestCaseModal(false)}
                    onCreated={handleTestCaseCreated}
                />
            )}

            <Modal
                show={moveDialog.open}
                onHide={() => setMoveDialog({ open: false, test: null, tests: [], value: '', saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {(moveDialog.tests?.length || 0) > 1 ? `Move ${moveDialog.tests?.length} Tests` : 'Move Test'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        {(moveDialog.tests?.length || 0) > 1
                            ? `Select an existing folder or type a new folder path for ${moveDialog.tests?.length} selected tests. New folders are created automatically after moving.`
                            : 'Select an existing folder from the dropdown or type a new folder path. New folders are created automatically after moving.'}
                    </p>
                    <FolderPathSelector
                        value={moveDialog.value}
                        onChange={(value) => setMoveDialog((previous) => ({ ...previous, value, error: null }))}
                        directory={workspace?.directory}
                        placeholder="All Test Cases / Regression / Authentication"
                        disabled={moveDialog.saving}
                    />
                    {moveDialog.error && <div className="rrw-modal-error">{moveDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setMoveDialog({ open: false, test: null, tests: [], value: '', saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => void handleSaveMoveDialog()} disabled={moveDialog.saving}>
                        {moveDialog.saving ? <Spinner animation="border" size="sm" /> : ((moveDialog.tests?.length || 0) > 1 ? `Move ${moveDialog.tests?.length} Tests` : 'Move Test')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={renameDialog.open}
                onHide={() => setRenameDialog({ open: false, folder: null, value: '', saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Rename Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        Rename {formatFolderPathForDisplay(renameDialog.folder?.path)}.
                    </p>
                    <Form.Group controlId="rename-folder-name">
                        <Form.Label>Folder name</Form.Label>
                        <Form.Control
                            value={renameDialog.value}
                            onChange={(event) => setRenameDialog((previous) => ({ ...previous, value: event.target.value, error: null }))}
                            placeholder="Authentication"
                            disabled={renameDialog.saving}
                            autoFocus
                        />
                    </Form.Group>
                    {renameDialog.error && <div className="rrw-modal-error">{renameDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setRenameDialog({ open: false, folder: null, value: '', saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => void handleSaveRenameDialog()} disabled={renameDialog.saving}>
                        {renameDialog.saving ? <Spinner animation="border" size="sm" /> : 'Rename Folder'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={deleteTestDialog.open}
                onHide={() => setDeleteTestDialog({ open: false, test: null, saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Test Case</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        Delete {deleteTestDialog.test?.title || deleteTestDialog.test?.test_key}? This removes the test case and its active history, cache, search, execution-summary, and local ADO link data.
                    </p>
                    {deleteTestDialog.error && <div className="rrw-modal-error">{deleteTestDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setDeleteTestDialog({ open: false, test: null, saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={() => void handleConfirmDeleteTestDialog()} disabled={deleteTestDialog.saving}>
                        {deleteTestDialog.saving ? <Spinner animation="border" size="sm" /> : 'Delete Test Case'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={deleteFolderDialog.open}
                onHide={() => setDeleteFolderDialog({ open: false, folder: null, saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        Delete {formatFolderPathForDisplay(deleteFolderDialog.folder?.path)} and {deleteFolderDialog.folder?.counts.total || 0} test case{(deleteFolderDialog.folder?.counts.total || 0) === 1 ? '' : 's'} inside it? This cannot be undone locally.
                    </p>
                    {deleteFolderDialog.error && <div className="rrw-modal-error">{deleteFolderDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setDeleteFolderDialog({ open: false, folder: null, saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={() => void handleConfirmDeleteFolderDialog()} disabled={deleteFolderDialog.saving}>
                        {deleteFolderDialog.saving ? <Spinner animation="border" size="sm" /> : 'Delete Folder'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default RequirementTestWorkspace;
